import moment, { Moment } from 'moment'
import { Image } from './Image'
import { InteractiveArea } from './InteractiveArea'
import { Project, ProjectDTO } from './Project'

export interface PersonDTO {
  createdDate: number
  externalId: string
  id: number
  images: Image[]
  info: string
  interactiveAreas: InteractiveArea[]
  loadContents: boolean
  mainImage: Image
  name: string
  ownerId: string
  projectsActed: ProjectDTO[]
  projectsDirected: ProjectDTO[]
  projectsProduced: ProjectDTO[]
  relatedMedia: ProjectDTO[]
  shotsCount: number
  status: string
  tags: string
}

export enum PersonRoles {
  ACTOR = 'Actor',
  DIRECTOR = 'Director',
  PRODUCER = 'Producer'
}

export class Person {
  createdDate: Moment
  externalId: string
  id: number
  images: Image[]
  info: string
  interactiveAreas: InteractiveArea[]
  loadContents: boolean
  mainImage: Image
  name: string
  ownerId: string
  projectsActed: Project[]
  projectsDirected: Project[]
  projectsProduced: Project[]
  relatedMedia: Project[]
  shotsCount: number
  status: string
  tags: string

  constructor (person: PersonDTO) {
    for (const property in person) {
      switch (property) {
        case 'createdDate': {
          this.createdDate = moment(person.createdDate)
          break
        }
        case 'projectsActed':
        case 'projectsDirected':
        case 'projectsProduced':
        case 'relatedMedia': {
          this[property] = (person[property] || []).map(p => new Project(p))
          break
        }
        default: {
          this[property] = person[property]
          break
        }
      }
    }
  }

  get roles (): PersonRoles[] {
    const results: PersonRoles[] = []

    if (this.projectsActed && this.projectsActed.length) results.push(PersonRoles.ACTOR)
    if (this.projectsDirected &&
      this.projectsDirected.length) results.push(PersonRoles.DIRECTOR)
    if (this.projectsProduced &&
      this.projectsProduced.length) results.push(PersonRoles.PRODUCER)

    return results
  }

  get rolesAsString (): string {
    return this.roles.join(', ')
  }

  get rolesByProjectId (): Map<number, Set<PersonRoles>> {
    const result = new Map<number, Set<PersonRoles>>()

    const handleProjects = (projects: Project[], role: PersonRoles) => {
      if (!projects || !projects.length) return
      projects.forEach(p => {
        const roles = result.get(p.id) || new Set()
        roles.add(role)
        result.set(p.id, roles)

        handleProjects(p.children, role)
      })
    }

    handleProjects(this.projectsActed, PersonRoles.ACTOR)
    handleProjects(this.projectsDirected, PersonRoles.DIRECTOR)
    handleProjects(this.projectsProduced, PersonRoles.PRODUCER)

    return result
  }

  getRolesByProjectId = (projectId: number) => {
    return Array.from(this.rolesByProjectId.get(projectId).values()).join(', ')
  }

  get projects (): Project[] {
    const projects = new Map<number, Project>()

    if (this.projectsActed && this.projectsActed.length) {
      this.projectsActed.forEach(p => projects.set(p.id, p))
    }
    if (this.projectsDirected && this.projectsDirected.length) {
      this.projectsDirected.forEach(p => projects.set(p.id, p))
    }
    if (this.projectsProduced && this.projectsProduced.length) {
      this.projectsProduced.forEach(p => projects.set(p.id, p))
    }

    return Array.from(projects.values())
  }

  get projectsInfoCol (): { projectName: string, additionalCount: number } {
    if (!this.projects || !this.projects.length) {
      return {
        projectName: null,
        additionalCount: null
      }
    }

    return {
      projectName: this.projects[0].name,
      additionalCount: this.projects.length - 1
    }
  }

  get asDTO (): PersonDTO {
    return {
      createdDate: this.createdDate.valueOf(),
      externalId: this.externalId,
      id: this.id,
      images: this.images,
      info: this.info,
      interactiveAreas: this.interactiveAreas,
      loadContents: this.loadContents,
      mainImage: this.mainImage,
      name: this.name,
      ownerId: this.ownerId,
      projectsActed: (this.projectsActed || []).map(p => p.asDTO),
      projectsDirected: (this.projectsDirected || []).map(p => p.asDTO),
      projectsProduced: (this.projectsProduced || []).map(p => p.asDTO),
      relatedMedia: (this.relatedMedia || []).map(p => p.asDTO),
      shotsCount: this.shotsCount,
      status: this.status,
      tags: this.tags
    }
  }
}
